import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getEvent, unregisterFromEvent, registerToEvent, confirmRegistration, getEventRegistrations, createPlayerByOrganizer, getCurrentUser } from '../services/api';
import PlayerForm from '../components/player/PlayerForm';
import PaymentModal from '../components/payment/PaymentModal';
import LoadingSpinner from '../components/shared/LoadingSpinner';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import toast from 'react-hot-toast';

/**
 * EventDetail - Página de detalle de un pozo
 */
const EventDetail = () => {
    const { eventId } = useParams();
    const navigate = useNavigate();
    const userId = localStorage.getItem('userId');
    const queryClient = useQueryClient();

    const { data: currentUser } = useQuery({
        queryKey: ['me'],
        queryFn: getCurrentUser
    });

    const {
        data: response,
        isLoading,
        isError
    } = useQuery({
        queryKey: ['event', eventId],
        queryFn: () => getEvent(eventId),
        retry: 1
    });

    // Obtener inscripciones para verificar si el usuario ya está apuntado
    const { data: registrations = [] } = useQuery({
        queryKey: ['registrations', eventId],
        queryFn: () => getEventRegistrations(eventId),
        enabled: !!eventId
    });

    const unregisterMutation = useMutation({
        mutationFn: () => unregisterFromEvent(eventId),
        onSuccess: () => {
            toast.success('Te has dado de baja del pozo');
            queryClient.invalidateQueries(['event', eventId]);
            queryClient.invalidateQueries(['registrations', eventId]);
        },
        onError: (error) => {
            toast.error(error.response?.data?.message || 'Error al cancelar la inscripción');
        }
    });

    const [isPaymentModalOpen, setIsPaymentModalOpen] = React.useState(false);

    const confirmRegistrationMutation = useMutation({
        mutationFn: (paymentData) => confirmRegistration(eventId, paymentData),
        onSuccess: () => {
            toast.success('¡Registro confirmado y pagado!');
            setIsPaymentModalOpen(false);
            queryClient.invalidateQueries(['event', eventId]);
            queryClient.invalidateQueries(['registrations', eventId]);
        },
        onError: (error) => {
            toast.error('Error al confirmar el registro en el servidor');
            console.error(error);
        }
    });

    const [partnerName, setPartnerName] = React.useState('');

    const registerMutation = useMutation({
        mutationFn: (customData) => {
            const price = parseFloat(response?.pricePerPlayer || 0);
            const regData = customData || {
                status: price > 0 ? 'pending_payment' : 'confirmed',
                player1Id: userId,
                priceSnapshot: price,
                partnerName: partnerName.trim()
            };
            return registerToEvent(eventId, regData);
        },
        onSuccess: (data) => {
            const price = parseFloat(response?.pricePerPlayer || 0);
            if (!isOrganizerView && price > 0 && data?.registration?.status === 'pending_payment') {
                // Modal already open via handleRegister (Optimistic UI)
            } else {
                toast.success('¡Inscripción realizada con éxito!');
                queryClient.invalidateQueries(['event', eventId]);
                queryClient.invalidateQueries(['registrations', eventId]);
                setPartnerName('');
                setIsOrganizerModalOpen(false);
            }
        },
        onError: (error) => {
            setIsPaymentModalOpen(false);
            const msg = error.message === 'EL_JUGADOR_YA_ESTA_INSCRITO' 
                ? 'Ya estás inscrito en este pozo' 
                : (error.response?.data?.message || 'Error al inscribirse');
            toast.error(msg);
        }
    });

    const [isOrganizerModalOpen, setIsOrganizerModalOpen] = React.useState(false);
    const [manualPaymentData, setManualPaymentData] = React.useState({
        method: 'efectivo',
        status: 'pagado'
    });

    const organizerRegisterMutation = useMutation({
        mutationFn: async (playerData) => {
            // 1. Crear el jugador (Ghost user)
            const newPlayer = await createPlayerByOrganizer({ 
                ...playerData, 
                organizerId: userId 
            });
            // 2. Inscribirlo en el evento
            return registerMutation.mutateAsync({
                status: 'confirmed', // Organizador inscribe manualmente (presumiblemente pagado o cortesía)
                player1Id: newPlayer.id,
                priceSnapshot: parseFloat(response?.pricePerPlayer || 0),
                isManualRegistration: true,
                paymentMethod: manualPaymentData.method,
                paymentStatus: manualPaymentData.status,
                recordedByOrganizer: userId
            });
        },
        onSuccess: () => {
            toast.success('Jugador registrado e inscrito');
        },
        onError: (err) => {
            toast.error('Error al realizar el registro manual');
            console.error(err);
        }
    });

    if (isLoading) return <div className="flex justify-center py-20"><LoadingSpinner /></div>;

    const event = response;

    if (isError || !event) {
        return (
            <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">Pozo no encontrado</h2>
                <button
                    onClick={() => navigate('/events')}
                    className="text-primary-600 hover:underline"
                >
                    Volver al listado
                </button>
            </div>
        );
    }

    const { title, club, date, startTime, pricePerPlayer, levelMin, levelMax, description, posterUrl, cancellationDeadlineHours } = event;
    
    // Validar datos críticos
    const clubData = club || { name: 'Club no disponible', city: '', address: '', photoUrl: null };
    const eventDate = date || new Date(); // Fallback por si no fecha

    // Check if user is registered using the fetched registrations
    const isRegistered = registrations.some(r => 
        (r.player1Id === userId || r.player2Id === userId) && 
        ['confirmed', 'pending_payment'].includes(r.status)
    );

    const handleUnregister = () => {
        if (window.confirm('¿Estás seguro de que quieres darte de baja? Si queda menos del plazo establecido no podrás hacerlo.')) {
            unregisterMutation.mutate();
        }
    };

    const handleRegister = () => {
        const price = parseFloat(pricePerPlayer || 0);
        if (price > 0) {
            setIsPaymentModalOpen(true); // Optimistic UI: Open immediate
        }
        registerMutation.mutate();
    };

    const isOrganizerView = currentUser?.id === event?.organizerId || currentUser?.role === 'organizer';

    return (
        <div className="min-h-screen bg-gray-50 pb-12">
            {/* Header Image */}
            <div className="h-64 bg-primary-600 relative overflow-hidden">
                <img
                    src={posterUrl || clubData.photoUrl || '/default-club.jpg'}
                    alt={clubData.name}
                    className="w-full h-full object-cover opacity-80"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-gray-900/80 to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-6 md:p-12 text-white container mx-auto">
                    <h1 className="text-2xl md:text-4xl font-bold mb-2 break-words">{title}</h1>
                    <p className="text-xl opacity-90">{clubData.name}</p>
                </div>
            </div>

            <div className="container mx-auto px-4 -mt-8 relative z-10">
                <div className="bg-white rounded-xl shadow-card p-6 md:p-8">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-10">

                        {/* LEFT COLUMN: Poster & Info */}
                        <div className="space-y-8">
                            {/* Poster if available */}
                            {posterUrl && (
                                <div className="rounded-2xl overflow-hidden shadow-lg border border-gray-100 group">
                                    <img
                                        src={posterUrl}
                                        alt={`Cartel ${title}`}
                                        className="w-full h-auto object-contain bg-gray-50 transition-transform duration-700 group-hover:scale-105"
                                    />
                                </div>
                            )}

                            {/* ATTRACTIVE INFO GRID */}
                            <div>
                                <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-3">
                                    <span className="bg-primary-100 p-2 rounded-xl text-2xl">📋</span>
                                    <span>Detalles del Evento</span>
                                </h2>

                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                    {/* Date */}
                                    <div className="bg-gray-50 p-4 rounded-2xl border border-gray-100 hover:border-primary-200 hover:shadow-md transition-all group">
                                        <div className="text-xl md:text-2xl mb-2 group-hover:scale-110 transition-transform origin-left">📅</div>
                                        <div className="text-[10px] text-gray-500 uppercase tracking-wider font-bold">Fecha</div>
                                        <div className="font-bold text-gray-900 capitalize text-base md:text-lg leading-tight">
                                            {format(new Date(eventDate), "EEE d MMM", { locale: es })}
                                        </div>
                                    </div>

                                    {/* Time */}
                                    <div className="bg-gray-50 p-4 rounded-2xl border border-gray-100 hover:border-primary-200 hover:shadow-md transition-all group">
                                        <div className="text-xl md:text-2xl mb-2 group-hover:scale-110 transition-transform origin-left">⏰</div>
                                        <div className="text-[10px] text-gray-500 uppercase tracking-wider font-bold">Horario</div>
                                        <div className="font-bold text-gray-900 text-base md:text-lg leading-tight">
                                            {startTime}
                                        </div>
                                    </div>

                                    {/* Level */}
                                    <div className="bg-gray-50 p-4 rounded-2xl border border-gray-100 hover:border-primary-200 hover:shadow-md transition-all group">
                                        <div className="text-xl md:text-2xl mb-2 group-hover:scale-110 transition-transform origin-left">⚖️</div>
                                        <div className="text-[10px] text-gray-500 uppercase tracking-wider font-bold">Nivel</div>
                                        <div className="font-bold text-gray-900 text-base md:text-lg leading-tight">
                                            {levelMin} - {levelMax}
                                        </div>
                                    </div>

                                    {/* Price */}
                                    <div className="bg-gray-50 p-4 rounded-2xl border border-gray-100 hover:border-primary-200 hover:shadow-md transition-all group">
                                        <div className="text-xl md:text-2xl mb-2 group-hover:scale-110 transition-transform origin-left">💰</div>
                                        <div className="text-[10px] text-gray-500 uppercase tracking-wider font-bold">Precio</div>
                                        <div className="font-bold text-primary-600 text-base md:text-lg leading-tight">
                                            {pricePerPlayer}€
                                        </div>
                                    </div>
                                </div>

                                {/* Location Full Width */}
                                <div className="mt-4 bg-gray-50 p-5 rounded-2xl border border-gray-100 hover:border-primary-200 hover:shadow-md transition-all group">
                                    <div className="flex items-start gap-4">
                                        <div className="text-3xl group-hover:scale-110 transition-transform mt-1">📍</div>
                                        <div className="flex-1">
                                            <div className="text-xs text-gray-500 uppercase tracking-wider font-bold mb-1">Ubicación</div>
                                            <div className="font-bold text-gray-900 text-lg">{clubData.name}</div>
                                            <div className="text-gray-600 leading-tight">{clubData.address}, {clubData.city}</div>
                                            <a
                                                href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(clubData.name + ' ' + clubData.city)}`}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="text-xs text-primary-600 font-bold hover:underline mt-2 inline-flex items-center gap-1"
                                            >
                                                Ver en el mapa
                                                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" /></svg>
                                            </a>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Right Column: CTA & Description */}
                        <div className="space-y-8">
                            <div className="bg-gray-50 rounded-2xl p-8 border border-gray-100 flex flex-col justify-center items-center text-center sticky top-24 shadow-sm">
                                {isRegistered ? (
                                    <>
                                        <h3 className="text-2xl font-bold mb-2 text-primary-900">¡Estás inscrito!</h3>
                                        <p className="text-gray-500 mb-8">Tienes tu plaza asegurada.</p>
                                        <button
                                            onClick={handleUnregister}
                                            disabled={unregisterMutation.isPending}
                                            className="w-full bg-red-100 text-red-600 font-black py-4 px-6 rounded-xl transition-all transform hover:-translate-y-1 shadow-sm hover:bg-red-200 text-lg uppercase tracking-wide border border-red-200"
                                        >
                                            {unregisterMutation.isPending ? 'Cancelando...' : 'Cancelar Inscripción'}
                                        </button>
                                        <p className="text-xs text-gray-400 mt-4 leading-relaxed">
                                            Puedes cancelar gratuitamente hasta {cancellationDeadlineHours || 24} horas antes del evento.
                                        </p>
                                    </>
                                ) : (
                                    <>
                                        <h3 className="text-2xl font-bold mb-2 text-gray-900">¡Inscríbete ahora!</h3>
                                        <p className="text-gray-500 mb-8">Quedan pocas plazas disponibles para este evento.</p>
                                        
                                        {/* Partner Name Input */}
                                        <div className="w-full mb-4 text-left">
                                            <label htmlFor="partnerName" className="block text-sm font-bold text-gray-700 mb-1">
                                                ¿Juegas con pareja?
                                            </label>
                                            <input
                                                type="text"
                                                id="partnerName"
                                                placeholder="Nombre de tu pareja (Opcional)"
                                                value={partnerName}
                                                onChange={(e) => setPartnerName(e.target.value)}
                                                className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-primary-500 focus:ring-2 focus:ring-primary-200 outline-none transition-all"
                                            />
                                        </div>

                                        <button
                                            onClick={handleRegister}
                                            disabled={registerMutation.isPending}
                                            className="w-full bg-primary-600 text-white font-black py-4 px-6 rounded-xl transition-all transform hover:-translate-y-1 shadow-lg hover:bg-primary-700 text-lg uppercase tracking-wide border-2 border-white/10"
                                        >
                                            {registerMutation.isPending ? 'Procesando...' : `Pagar ${pricePerPlayer}€ y Unirse`}
                                        </button>
                                        <div className="flex items-center gap-2 mt-6 text-xs text-gray-400">
                                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" /></svg>
                                            Pago 100% seguro garantizado
                                        </div>
                                    </>
                                )}

                                {isOrganizerView && (
                                    <div className="w-full mt-6 pt-6 border-t border-gray-100">
                                        <button
                                            onClick={() => setIsOrganizerModalOpen(true)}
                                            className="w-full bg-slate-900 text-white font-bold py-3 px-6 rounded-xl transition-all hover:bg-slate-800 flex items-center justify-center gap-2 text-sm uppercase tracking-widest"
                                        >
                                            <span>👤</span> Inscribir Manualmente
                                        </button>
                                        <p className="text-[10px] text-gray-400 mt-2 font-bold uppercase text-center">Panel de Organizador</p>
                                    </div>
                                )}
                            </div>

                            {description && (
                                <div className="pt-8 border-t border-gray-100">
                                    <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                                        <span>📝</span> Acerca del evento
                                    </h3>
                                    <div className="bg-white p-6 rounded-2xl border border-gray-100 text-gray-600 leading-relaxed shadow-sm">
                                        {description}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            <PaymentModal
                isOpen={isPaymentModalOpen}
                onClose={() => setIsPaymentModalOpen(false)}
                event={event}
                onSuccess={(paymentIntent) => {
                    confirmRegistrationMutation.mutate({ paymentIntentId: paymentIntent?.id });
                }}
            />

            {/* Modal de Inscripción Manual (Organizador) */}
            {isOrganizerModalOpen && (
                <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-fade-in" onClick={() => setIsOrganizerModalOpen(false)}>
                    <div className="bg-white rounded-3xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col overflow-hidden animate-slide-up" onClick={e => e.stopPropagation()}>
                        <div className="p-6 border-b border-gray-100 flex justify-between items-center bg-gray-50/50">
                             <div>
                                <h3 className="font-black text-gray-900 uppercase tracking-widest italic text-lg">Inscribir Jugador</h3>
                                <p className="text-xs text-gray-400 font-bold uppercase tracking-wider mt-1">Crea un perfil e inscríbelo directamente en este pozo</p>
                             </div>
                             <button onClick={() => setIsOrganizerModalOpen(false)} className="w-10 h-10 rounded-full bg-white border border-gray-200 flex items-center justify-center text-gray-400 hover:text-gray-600">
                                ✕
                             </button>
                        </div>
                        <div className="flex-1 overflow-y-auto p-6 custom-scrollbar">
                             <div className="mb-8 p-4 bg-gray-50 rounded-2xl flex flex-col sm:flex-row gap-4">
                                <div className="flex-1">
                                    <label className="block text-[10px] font-bold text-gray-400 uppercase mb-2 ml-1">Forma de Pago</label>
                                    <div className="flex gap-2">
                                        {['efectivo', 'bizum', 'tarjeta'].map(m => (
                                            <button
                                                key={m}
                                                onClick={() => setManualPaymentData(p => ({ ...p, method: m }))}
                                                className={`flex-1 py-3 rounded-xl border-2 font-black text-[10px] uppercase transition-all ${manualPaymentData.method === m ? 'bg-primary-600 border-primary-600 text-white shadow-lg' : 'bg-white border-gray-100 text-gray-400'}`}
                                            >
                                                {m}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                                <div className="flex-1">
                                    <label className="block text-[10px] font-bold text-gray-400 uppercase mb-2 ml-1">Estado</label>
                                    <div className="flex gap-2">
                                        {['pagado', 'pendiente'].map(s => (
                                            <button
                                                key={s}
                                                onClick={() => setManualPaymentData(p => ({ ...p, status: s }))}
                                                className={`flex-1 py-3 rounded-xl border-2 font-black text-[10px] uppercase transition-all ${manualPaymentData.status === s ? 'bg-slate-900 border-slate-900 text-white shadow-lg' : 'bg-white border-gray-100 text-gray-400'}`}
                                            >
                                                {s === 'pagado' ? '✅ Pagado' : '⏳ Pend.'}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                             </div>

                             <PlayerForm 
                                onSubmit={(data) => organizerRegisterMutation.mutate(data)} 
                                isSubmitting={organizerRegisterMutation.isPending} 
                             />
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default EventDetail;
